import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { TrashIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, EyeIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardIcon, FunnelIcon, BookmarkIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { PaginatedResponse, SuppressionList, FilterGroup } from '../types'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import FilterBuilder from '../components/FilterBuilder'
import SaveSuppressionListModal from '../components/SaveSuppressionListModal'

interface SuppressionRow {
  id: string
  row_number: number
  data: Record<string, string>
  created_at: string
}

export default function SuppressionListsPage() {
  const queryClient = useQueryClient()
  const [showUpload, setShowUpload] = useState(false)
  const [selectedList, setSelectedList] = useState<SuppressionList | null>(null)
  const [viewingData, setViewingData] = useState(false)
  const [dataPage, setDataPage] = useState(1)
  const [uploadName, setUploadName] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Filter state
  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<FilterGroup | null>(null)
  const [filteredCount, setFilteredCount] = useState<number | null>(null)
  const [showSaveModal, setShowSaveModal] = useState(false)
  
  // Saved lists state
  const [viewingSavedList, setViewingSavedList] = useState<string | null>(null)
  const [savedListPage, setSavedListPage] = useState(1)
  
  const { data, isLoading } = useQuery({
    queryKey: ['suppression-lists'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<SuppressionList>>('/suppression-lists')
      return res.data
    },
    refetchInterval: (query) => {
      // Refetch every 2 seconds if any list is processing
      const hasProcessing = query.state.data?.items.some(
        item => item.status === 'processing' || item.status === 'pending'
      )
      return hasProcessing ? 2000 : false
    },
  })

  // Fetch rows when viewing data (with optional filter)
  const { data: rowsData, isLoading: rowsLoading } = useQuery({
    queryKey: ['suppression-rows', selectedList?.id, dataPage, filter],
    queryFn: async () => {
      if (!selectedList) return null
      
      if (filter) {
        // Use filter endpoint
        const res = await api.post<PaginatedResponse<SuppressionRow>>(
          `/suppression-lists/${selectedList.id}/rows/filter`,
          { filter, page: dataPage, page_size: 50 }
        )
        return res.data
      } else {
        // Use normal endpoint
        const res = await api.get<PaginatedResponse<SuppressionRow>>(
          `/suppression-lists/${selectedList.id}/rows`,
          { params: { page: dataPage, page_size: 50 } }
        )
        return res.data
      }
    },
    enabled: !!selectedList && viewingData && !viewingSavedList,
  })
  
  // Fetch saved lists
  const { data: savedLists } = useQuery({
    queryKey: ['saved-suppression-lists', selectedList?.id],
    queryFn: async () => {
      if (!selectedList) return []
      const res = await api.get(`/suppression-lists/${selectedList.id}/saved-lists`)
      return res.data
    },
    enabled: !!selectedList,
  })
  
  // Fetch saved list rows
  const { data: savedListRows, isLoading: savedListRowsLoading } = useQuery({
    queryKey: ['saved-suppression-list-rows', viewingSavedList, savedListPage],
    queryFn: async () => {
      if (!viewingSavedList) return null
      const res = await api.get<PaginatedResponse<SuppressionRow>>(
        `/saved-suppression-lists/${viewingSavedList}/rows`,
        { params: { page: savedListPage, page_size: 50 } }
      )
      return res.data
    },
    enabled: !!viewingSavedList,
  })
  
  // Filter count mutation
  const filterCountMutation = useMutation({
    mutationFn: async (f: FilterGroup) => {
      if (!selectedList) throw new Error('No list selected')
      const res = await api.post<{ count: number }>(
        `/suppression-lists/${selectedList.id}/rows/filter/count`,
        { filter: f }
      )
      return res.data.count
    },
    onSuccess: (count) => {
      setFilteredCount(count)
    },
  })
  
  // Delete saved list mutation
  const deleteSavedListMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/saved-suppression-lists/${id}`)
    },
    onSuccess: () => {
      toast.success('Saved list deleted')
      queryClient.invalidateQueries({ queryKey: ['saved-suppression-lists', selectedList?.id] })
      if (viewingSavedList) {
        setViewingSavedList(null)
      }
    },
    onError: () => {
      toast.error('Failed to delete saved list')
    },
  })
  
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error('No file selected')
      
      const formData = new FormData()
      formData.append('file', selectedFile)
      if (uploadName) formData.append('name', uploadName)
      if (uploadDescription) formData.append('description', uploadDescription)
      
      await api.post('/suppression-lists/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    onSuccess: () => {
      toast.success('Suppression list uploaded! Processing...')
      queryClient.invalidateQueries({ queryKey: ['suppression-lists'] })
      setShowUpload(false)
      setUploadName('')
      setUploadDescription('')
      setSelectedFile(null)
    },
    onError: () => {
      toast.error('Failed to upload suppression list')
    },
  })
  
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/suppression-lists/${id}`)
    },
    onSuccess: () => {
      toast.success('Suppression list deleted')
      queryClient.invalidateQueries({ queryKey: ['suppression-lists'] })
      setSelectedList(null)
    },
    onError: () => {
      toast.error('Failed to delete suppression list')
    },
  })
  
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      if (!uploadName) {
        setUploadName(file.name.replace(/\.csv$/i, ''))
      }
    }
  }
  
  const copyToClipboard = (text: string, label: string = 'ID') => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied to clipboard`)
  }

  const handleExport = async (list: SuppressionList) => {
    try {
      const res = await api.get(`/suppression-lists/${list.id}/export`, {
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${list.name || 'suppression_list'}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Export started')
    } catch {
      toast.error('Failed to export suppression list')
    }
  }
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Ready</span>
      case 'processing':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 animate-pulse">Processing</span>
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Pending</span>
      case 'error':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Error</span>
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>
    }
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Suppression Lists</h1>
          <p className="mt-1 text-sm text-gray-600">
            Upload CSV files to exclude records from exports
          </p>
        </div>
        <button onClick={() => setShowUpload(true)} className="btn-primary">
          <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
          Upload CSV
        </button>
      </div>
      
      {/* Upload form */}
      {showUpload && (
        <div className="card p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Suppression List</h3>
          <div className="space-y-4">
            <div>
              <label className="label">CSV File</label>
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {selectedFile ? (
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div>
                    <ArrowUpTrayIcon className="h-8 w-8 mx-auto text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">Click to select a CSV file</p>
                    <p className="text-xs text-gray-400">The CSV can have multiple columns (email, phone, domain, etc.)</p>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                  className="input"
                  placeholder="e.g., Email Unsubscribes"
                />
              </div>
              <div>
                <label className="label">Description (optional)</label>
                <input
                  type="text"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  className="input"
                  placeholder="Optional description"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button 
              onClick={() => {
                setShowUpload(false)
                setSelectedFile(null)
                setUploadName('')
                setUploadDescription('')
              }} 
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => uploadMutation.mutate()}
              disabled={!selectedFile || uploadMutation.isPending}
              className="btn-primary"
            >
              {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      )}
      
      {/* Details panel */}
      {selectedList && (
        <div className="card p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-gray-900">{selectedList.name}</h3>
                <button
                  onClick={() => copyToClipboard(selectedList.id, 'List ID')}
                  className="text-gray-400 hover:text-gray-600"
                  title="Copy list ID"
                >
                  <ClipboardIcon className="h-5 w-5" />
                </button>
              </div>
              {selectedList.description && (
                <p className="text-sm text-gray-500">{selectedList.description}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">ID: {selectedList.id}</p>
            </div>
            <button onClick={() => { setSelectedList(null); setViewingData(false); setDataPage(1); }} className="text-gray-400 hover:text-gray-600 text-xl">
              ×
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Status</p>
              <p className="mt-1">{getStatusBadge(selectedList.status)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Unique Rows</p>
              <p className="mt-1 text-lg font-semibold">
                {(selectedList.unique_row_count ?? selectedList.row_count).toLocaleString()}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">Columns</p>
              <p className="mt-1 text-lg font-semibold">{selectedList.column_count}</p>
            </div>
          </div>
          
          {selectedList.columns && selectedList.columns.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Columns:</p>
              <div className="flex flex-wrap gap-2">
                {selectedList.columns.map((col) => (
                  <span 
                    key={col.id} 
                    className="px-2 py-1 text-xs bg-gray-100 rounded-md"
                    title={`Type: ${col.column_type}, Unique: ${col.unique_count?.toLocaleString() || 'N/A'}`}
                  >
                    {col.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Export is always available from details view */}
          {!viewingSavedList && (
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => handleExport(selectedList)}
                className="btn-secondary"
                title="Download entire list as CSV"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Export CSV
              </button>
            </div>
          )}

          {/* View Data Button */}
          {selectedList.status === 'ready' && !viewingData && !viewingSavedList && (
            <div className="flex gap-2">
              <button
                onClick={() => setViewingData(true)}
                className="btn-secondary"
              >
                <EyeIcon className="h-5 w-5 mr-2" />
                View Data
              </button>
              <button
                onClick={() => setShowFilter(!showFilter)}
                className="btn-secondary"
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                {showFilter ? 'Hide Filter' : 'Show Filter'}
              </button>
            </div>
          )}
          
          {/* Filter Builder */}
          {showFilter && selectedList.columns && !viewingSavedList && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">Filter Rows</h4>
                {filter && (
                  <button
                    onClick={() => {
                      setFilter(null)
                      setFilteredCount(null)
                      setDataPage(1)
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
              
              <FilterBuilder
                columns={selectedList.columns}
                value={filter}
                onChange={(newFilter) => {
                  setFilter(newFilter)
                  setDataPage(1)
                  if (newFilter && newFilter.and && newFilter.and.length > 0) {
                    filterCountMutation.mutate(newFilter)
                  } else {
                    setFilteredCount(null)
                  }
                }}
              />
              
              {filteredCount !== null && (
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    {filteredCount.toLocaleString()} rows match this filter
                  </p>
                  <button
                    onClick={() => {
                      setViewingData(true)
                      setShowSaveModal(true)
                    }}
                    className="btn-primary text-sm"
                    disabled={!filter || filteredCount === 0}
                  >
                    <BookmarkIcon className="h-4 w-4 mr-1" />
                    Save as List
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Saved Lists Section */}
          {savedLists && savedLists.length > 0 && !viewingSavedList && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Saved Lists</h4>
              <div className="space-y-2">
                {savedLists.map((list: any) => (
                  <div
                    key={list.id}
                    className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-xl">
                        {list.list_type === 'static' ? '📸' : '🔄'}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{list.name}</p>
                        {list.description && (
                          <p className="text-xs text-gray-500">{list.description}</p>
                        )}
                        <p className="text-xs text-gray-400">
                          {list.list_type === 'static' ? 'Static snapshot' : 'Dynamic filter'} • 
                          {list.cached_count?.toLocaleString() || '0'} rows
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setViewingSavedList(list.id)
                          setSavedListPage(1)
                          setViewingData(false)
                        }}
                        className="text-primary-600 hover:text-primary-700"
                        title="View rows"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete saved list "${list.name}"?`)) {
                            deleteSavedListMutation.mutate(list.id)
                          }
                        }}
                        className="text-red-600 hover:text-red-700"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Viewing Saved List */}
          {viewingSavedList && savedListRows && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Saved List: {savedLists?.find((l: any) => l.id === viewingSavedList)?.name}
                </h4>
                <button
                  onClick={() => {
                    setViewingSavedList(null)
                    setSavedListPage(1)
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Close
                </button>
              </div>

              {savedListRowsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : savedListRows.items.length > 0 ? (
                <>
                  <div className="overflow-auto border rounded-lg max-h-96">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          {selectedList.columns?.map((col) => (
                            <th key={col.id} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              {col.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {savedListRows.items.map((row: any) => (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-400">{row.row_number}</td>
                            {selectedList.columns?.map((col) => (
                              <td key={col.id} className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.data[col.name]}>
                                {row.data[col.name] || <span className="text-gray-300">—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {savedListRows.pages > 1 && (
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-sm text-gray-500">
                        Page {savedListRows.page} of {savedListRows.pages} ({savedListRows.total.toLocaleString()} rows)
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSavedListPage(p => Math.max(1, p - 1))}
                          disabled={savedListPage === 1}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setSavedListPage(p => Math.min(savedListRows.pages, p + 1))}
                          disabled={savedListPage === savedListRows.pages}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 py-4">No rows in this saved list</p>
              )}
            </div>
          )}

          {/* Data Table */}
          {viewingData && !viewingSavedList && (
            <div className="mt-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium text-gray-700">Data Preview</h4>
                <button
                  onClick={() => { setViewingData(false); setDataPage(1); }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Hide
                </button>
              </div>

              {rowsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : rowsData?.items.length ? (
                <>
                  <div className="overflow-auto border rounded-lg max-h-96">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          {selectedList.columns?.map((col) => (
                            <th key={col.id} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              {col.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {rowsData.items.map((row) => (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-400">{row.row_number}</td>
                            {selectedList.columns?.map((col) => (
                              <td key={col.id} className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row.data[col.name]}>
                                {row.data[col.name] || <span className="text-gray-300">—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {rowsData.pages > 1 && (
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-sm text-gray-500">
                        Page {rowsData.page} of {rowsData.pages} ({rowsData.total.toLocaleString()} rows)
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setDataPage(p => Math.max(1, p - 1))}
                          disabled={dataPage === 1}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setDataPage(p => Math.min(rowsData.pages, p + 1))}
                          disabled={dataPage === rowsData.pages}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 py-4">No data in this suppression list</p>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* List */}
      {data?.items.length ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unique Rows
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Columns
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.items.map((list) => (
                <tr key={list.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{list.name}</p>
                    {list.description && (
                      <p className="text-xs text-gray-500">{list.description}</p>
                    )}
                    {list.source_filename && (
                      <p className="text-xs text-gray-400">{list.source_filename}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(list.status)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {(list.unique_row_count ?? list.row_count).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {list.column_count}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDistanceToNow(new Date(list.created_at), { addSuffix: true })}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => copyToClipboard(list.id, 'List ID')}
                      className="text-gray-600 hover:text-gray-700"
                      title="Copy list ID"
                    >
                      <ClipboardIcon className="h-5 w-5 inline" />
                    </button>
                    <button
                      onClick={async () => {
                        // Fetch full details including columns
                        const res = await api.get<SuppressionList>(`/suppression-lists/${list.id}`)
                        setSelectedList(res.data)
                        setViewingData(false)
                        setDataPage(1)
                      }}
                      className="text-primary-600 hover:text-primary-700"
                      title="View details"
                    >
                      <EyeIcon className="h-5 w-5 inline" />
                    </button>
                    <button
                      onClick={() => handleExport(list)}
                      className="inline-flex items-center text-gray-600 hover:text-gray-700"
                      title="Export as CSV"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5 inline" />
                      <span className="ml-1 text-xs">Export</span>
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Delete this suppression list?')) {
                          deleteMutation.mutate(list.id)
                        }
                      }}
                      className="text-red-600 hover:text-red-700"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <ArrowUpTrayIcon className="h-12 w-12 mx-auto text-gray-400" />
          <p className="mt-4 text-gray-500">No suppression lists yet</p>
          <p className="text-sm text-gray-400 mb-4">Upload a CSV file to create your first suppression list</p>
          <button onClick={() => setShowUpload(true)} className="btn-primary">
            Upload CSV
          </button>
        </div>
      )}
      
      {/* Save List Modal */}
      {showSaveModal && selectedList && filter && (
        <SaveSuppressionListModal
          suppressionListId={selectedList.id}
          filter={filter}
          rowCount={filteredCount || 0}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </div>
  )
}
